import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/flights/airlines';

/**
 * getAirlines - imperative fetch (for on-demand search in modals/forms).
 * Returns the raw API payload { success, data, pagination }.
 */
export const getAirlines = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

/**
 * Map API airline to UI format
 * API: { id, name, iataCode, icaoCode, country, logo, active, ... }
 * UI:  { id, airlineName, iataCode, icaoCode, country, status }
 */
export const mapApiToUi = (api) => {
  if (!api) return null;
  const logoUrl = api.logo || null;
  return {
    id: api.id,
    airlineName: api.name ?? '',
    iataCode: api.iataCode ?? '',
    icaoCode: api.icaoCode ?? '',
    country: api.country ?? '',
    status: api.active ? 'active' : 'inactive',
    logo: logoUrl,
    logoUrl,
    logoSource: api.logoSource ?? null,
    checkinStatus: api.checkinStatus ?? '',
    checkinRequiredFields: (() => {
      try { return typeof api.checkinRequiredFields === 'string' ? JSON.parse(api.checkinRequiredFields) : (api.checkinRequiredFields || []); } catch { return []; }
    })(),
    checkinText: api.checkinText ?? '',
    checkinUrl: api.checkinUrl ?? '',
  };
};

/**
 * Map UI airline to API format for create/update
 */
export const mapUiToApi = (ui) => {
  let logo = ui.logo ?? null;
  // Don't include File objects in JSON payload — they're uploaded separately
  if (logo && typeof logo !== 'string') {
    logo = null;
  }
  return {
    name: String(ui.airlineName ?? ui.name ?? ''),
    iataCode: String(ui.iataCode ?? '').toUpperCase(),
    icaoCode: ui.icaoCode ? String(ui.icaoCode).toUpperCase() : null,
    country: ui.country ? String(ui.country) : null,
    logo,
    active: ui.status === 'active' || ui.active === true,
    checkinStatus: ui.checkinStatus || null,
    checkinRequiredFields: ui.checkinRequiredFields?.length ? ui.checkinRequiredFields : null,
    checkinText: ui.checkinText || null,
    checkinUrl: ui.checkinUrl || null,
  };
};

/**
 * Upload airline logo file, returns the URL
 */
const uploadLogo = async (airlineId, logoFile) => {
  const formData = new FormData();
  formData.append('logo', logoFile);
  const response = await axiosInstance.post(`${BASE}/${airlineId}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data?.data?.logoUrl || null;
};

/**
 * useGetAirlines - fetch airlines list with params
 * @param {Object} params - { page, limit, search, status }
 * @returns { data, pagination, loading, error, refetch }
 */
export const useGetAirlines = (params = {}) => {
  const {
    page = 1,
    limit = 100,
    search = '',
    status = '',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAirlines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { page, limit };
      if (search) queryParams.search = search;
      if (status && status !== 'all') {
        queryParams.status = status === 'active' ? 'Active' : 'Inactive';
      }

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        const mapped = (res.data || []).map(mapApiToUi);
        setData(mapped);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res.message || 'Failed to load airlines');
        setData([]);
      }
    } catch (err) {
      if (err.response) {
        setError(err.response?.data?.message || err.response?.data?.error || `Request failed (${err.response.status})`);
      } else if (err.request) {
        setError('Cannot reach server. Check that the backend is running (e.g. http://localhost:5000).');
      } else {
        setError(err.message || 'Failed to load airlines');
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status]);

  useEffect(() => {
    fetchAirlines();
  }, [fetchAirlines]);

  const refetch = useCallback(() => {
    fetchAirlines();
  }, [fetchAirlines]);

  return { data, pagination, loading, error, refetch };
};

/**
 * Create airline
 * @returns { execute(payload), loading, error }
 */
export const useCreateAirline = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const logoFile = payload.logo instanceof File ? payload.logo : null;
      const apiPayload = mapUiToApi(payload);
      const response = await axiosInstance.post(BASE, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        // Upload logo file if provided
        if (logoFile && res.data.id) {
          try {
            const logoUrl = await uploadLogo(res.data.id, logoFile);
            if (logoUrl) res.data.logo = logoUrl;
          } catch (e) {
            console.warn('Logo upload failed:', e.message);
          }
        }
        return { success: true, data: mapApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to create airline' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to create airline';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * Update airline
 * @returns { execute(id, payload), loading, error }
 */
export const useUpdateAirline = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const logoFile = payload.logo instanceof File ? payload.logo : null;
      const apiPayload = mapUiToApi(payload);
      const response = await axiosInstance.put(`${BASE}/${id}`, apiPayload);
      const res = response.data;
      if (res.success && res.data) {
        // Upload logo file if provided
        if (logoFile) {
          try {
            const logoUrl = await uploadLogo(id, logoFile);
            if (logoUrl) res.data.logo = logoUrl;
          } catch (e) {
            console.warn('Logo upload failed:', e.message);
          }
        }
        return { success: true, data: mapApiToUi(res.data) };
      }
      return { success: false, message: res.message || 'Failed to update airline' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update airline';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * Sync airline logos from the free logo CDNs into our own object storage.
 *
 * The backend works in batches so no single request hangs on a few hundred
 * downloads, so this keeps calling until the server reports nothing remaining.
 * `onProgress` is fired after each batch to drive the button label.
 *
 * @returns { execute({ mode, onProgress }), loading, error }
 */
export const useSyncAirlineLogos = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async ({ mode = 'missing', onProgress } = {}) => {
    setLoading(true);
    setError(null);
    const totals = { processed: 0, updated: 0, notFound: 0, failed: 0 };

    try {
      // Hard stop so a backend that never drains `remaining` can't spin forever.
      for (let batch = 0; batch < 60; batch += 1) {
        const response = await axiosInstance.post(`${BASE}/logos/sync`, { mode, limit: 40 });
        const res = response.data;
        if (!res.success) {
          return { success: false, message: res.message || 'Logo sync failed', totals };
        }

        const data = res.data || {};
        totals.processed += data.processed || 0;
        totals.updated += data.updated || 0;
        totals.notFound += data.notFound || 0;
        totals.failed += data.failed || 0;
        onProgress?.({ ...totals, remaining: data.remaining || 0 });

        if (!data.remaining || !data.processed) break;
      }

      return { success: true, totals };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Logo sync failed';
      setError(msg);
      return { success: false, message: msg, totals };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * Undo a logo sync — restores every logo that was replaced.
 * @returns { execute(), loading, error }
 */
export const useRestoreAirlineLogos = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${BASE}/logos/restore`);
      const res = response.data;
      if (res.success) {
        return { success: true, restored: res.data?.restored || 0, message: res.message };
      }
      return { success: false, message: res.message || 'Failed to restore logos' };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to restore logos';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * Delete airline
 * @returns { execute(id), loading, error }
 */
export const useDeleteAirline = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to delete airline';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};
